import { useEffect, useState } from 'react'
import type { SessionUser } from '@raporto/shared'
import { ActionButton } from '../ui/ActionButton.js'
import { useCacheTable } from '../ui/hooks.js'
import { RapportForm } from './Rapport.js'
import { db, type CacheRow } from '../net/localDb.js'
import { subscribe, syncNow, type SyncStatus } from '../net/syncEngine.js'
import { istGesperrt } from '../net/rapports.js'
import { needsAttention, retry } from '../net/outbox.js'
import type { OutboxEntry } from '../net/localDb.js'
import { notify, notifyError } from '../ui/Toasts.js'

/**
 * Die Startseite eines Monteurs.
 *
 * Sie beantwortet drei Fragen und sonst keine: wo bin ich eingeteilt, was
 * habe ich heute schon erfasst, und ist alles draussen. Ein Dashboard mit
 * Kennzahlen wäre hier falsch – auf der Baustelle zählt der nächste Griff,
 * nicht der Monatsverlauf.
 *
 * Der Erfassen-Knopf ist absichtlich der grösste Gegenstand auf dem Bildschirm.
 */

export function WorkerHome({ session }: { session: SessionUser }): JSX.Element {
  const { rows: projects } = useCacheTable<CacheRow>('projects')
  const { rows: customers } = useCacheTable<CacheRow>('customers')
  const { rows: rapports } = useCacheTable<CacheRow>('rapports')
  const [status, setStatus] = useState<SyncStatus | null>(null)
  const [erfassen, setErfassen] = useState<string | null>(null)
  const [fotos, setFotos] = useState({ total: 0, done: 0 })
  const [abgewiesen, setAbgewiesen] = useState<OutboxEntry[]>([])

  useEffect(() => subscribe(setStatus), [])

  useEffect(() => {
    void (async () => {
      const alle = await db.photos.toArray()
      setFotos({
        total: alle.length,
        done: alle.filter((p) => p.status === 'confirmed').length
      })
      // Abgewiesene Rapporte gehören ganz nach oben und mit Grund.
      const offen = await needsAttention()
      setAbgewiesen(offen.filter((e) => e.type.startsWith('rapport.')))
    })()
  }, [status])

  const offen = projects.filter((p) => p.status === 'offen')
  const kundeVon = (p: CacheRow): string =>
    String(customers.find((c) => c.id === p.customerId)?.name ?? '')

  const heute = new Date().toISOString().slice(0, 10)
  const heutige = rapports.filter((r) => r.workDate === heute)
  const stundenHeute = heutige.reduce((summe, r) => {
    const zeilen = (r.lines as Array<{ kind: string; quantity: number }> | undefined) ?? []
    return summe + zeilen.filter((l) => l.kind === 'stunden').reduce((s, l) => s + l.quantity, 0)
  }, 0)

  const wartend = rapports.filter((r) => r.pending === true).length

  if (erfassen) {
    return (
      <>
        <div className="card slim">
          <button type="button" id="btn-zurueck" className="backlink" onClick={() => setErfassen(null)}>
            ← Übersicht
          </button>
        </div>
        <RapportForm
          session={session}
          projectId={erfassen}
          onDone={() => setErfassen(null)}
          onCancel={() => setErfassen(null)}
        />
      </>
    )
  }

  return (
    <>
      <div className="card">
        <h1>Hallo {session.name.split(' ')[0]}</h1>
        <p className="muted">
          {offen.length === 0
            ? 'Dir ist zurzeit kein Projekt zugewiesen.'
            : `${offen.length} Projekt${offen.length > 1 ? 'e' : ''} für dich.`}
          {stundenHeute > 0 && ` Heute erfasst: ${stundenHeute} h.`}
        </p>
      </div>

      {abgewiesen.length > 0 && (
        <div className="card problemcard">
          <h2>Nicht angekommen</h2>
          <p className="muted small">
            Diese Rapporte hat der Server abgewiesen. Sie warten nicht auf Empfang – sie
            brauchen eine Korrektur.
          </p>
          <ul className="records">
            {abgewiesen.map((e) => (
              <li key={e.id}>
                <div className="line-row">
                  <span className="rec-name">
                    {(e.payload as { workDate?: string }).workDate ?? 'Rapport'}
                    <span className="pill small crit">abgewiesen</span>
                  </span>
                  <span className="rec-sub">{e.lastError ?? 'Unbekannter Grund.'}</span>
                  <button
                    type="button"
                    className="line-remove"
                    onClick={async () => {
                      await retry(e.id)
                      await syncNow({ force: true })
                      notify.info('Wird nochmal versucht.')
                    }}
                  >
                    Nochmal
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {offen.length > 0 && (
        <div className="card">
          <h2>Rapport erfassen</h2>
          <p className="muted small">Projekt antippen – der Rest geht in unter einer Minute.</p>
          <ul className="bigpick">
            {offen.map((p) => (
              <li key={String(p.id)}>
                <button
                  type="button"
                  id={`pick-${String(p.id)}`}
                  onClick={() => setErfassen(String(p.id))}
                >
                  <span className="bigpick-name">{String(p.name)}</span>
                  <span className="bigpick-sub">
                    {[kundeVon(p), [p.zip, p.city].filter(Boolean).join(' ')]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="card">
        <div className="listhead">
          <h2>Meine Rapporte</h2>
          <span className="muted">{rapports.length}</span>
        </div>

        {(wartend > 0 || fotos.total > 0) && (
          <p className="muted small">
            {wartend - abgewiesen.length > 0 &&
              `${wartend - abgewiesen.length} Rapport${
                wartend - abgewiesen.length > 1 ? 'e' : ''
              } warten auf Empfang. `}
            {fotos.total > 0 && `Fotos: ${fotos.done} von ${fotos.total} übertragen.`}
          </p>
        )}

        {rapports.length === 0 ? (
          <p className="muted">Noch nichts erfasst.</p>
        ) : (
          <ul className="records">
            {[...rapports]
              .sort((a, b) => String(b.workDate).localeCompare(String(a.workDate)))
              .slice(0, 20)
              .map((r) => {
                const projekt = projects.find((p) => p.id === r.projectId)
                const zeilen = (r.lines as Array<{ kind: string; quantity: number }> | undefined) ?? []
                const h = zeilen
                  .filter((l) => l.kind === 'stunden')
                  .reduce((s, l) => s + l.quantity, 0)
                return (
                  <li key={String(r.id)}>
                    <div className="line-row">
                      <span className="rec-name">
                        {String(projekt?.name ?? 'Projekt')}
                        {r.pending === true && <span className="pill small">wartet</span>}
                        {istGesperrt(r) && <span className="pill small">unterschrieben</span>}
                        {r.isRegie === true && <span className="pill small">Regie</span>}
                      </span>
                      <span className="rec-sub">
                        {String(r.workDate)}
                        {h > 0 ? ` · ${h} h` : ''}
                        {r.description ? ` · ${String(r.description).slice(0, 40)}` : ''}
                      </span>
                    </div>
                  </li>
                )
              })}
          </ul>
        )}

        <ActionButton
          id="btn-worker-sync"
          variant="secondary"
          labels={{ idle: 'Jetzt übertragen', busy: 'Überträgt …', success: 'Alles draussen' }}
          onAction={async () => {
            await syncNow({ force: true })
          }}
          onError={(err) => notifyError(err)}
        />
      </div>
    </>
  )
}
